import {
  PrimaryButton,
  SecondaryButton,
  CopyTextBox,
  RadioButton,
  WarningBanner,
} from "@graffer-inc/console-design-kit";
import { QRCodeCanvas } from "qrcode.react";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { connectPageService } from "../../../../application/domain/base/base-application-service";
import {
  MatrixCodeContextProps,
  MatrixCodeFileExtension,
  MatrixCodeService,
} from "../../../../application/service/graffer-utility-tools/matrix-code/matrix-code-service";
import { useModal } from "../../../../utils/custom-hooks";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import { GrafferUitlsTemplate } from "../../../template/graffer-utility-tools/graffer-utility-tools-template";
import { DownloadMatrixCodeModal } from "../modals/download-matrix-code-modal";
import { StaticFeatureFlags } from "../../../../feature-flags";

const S_SIZE = 128;
const M_SIZE = 256;
const L_SIZE = 512;

const SELECTED_INCLUDE_MARGIN = "あり";
const NOT_SELECTED_INCLUDE_MARGIN = "なし";

const JPEG = "jpeg";
const PNG = "png";

export const MatrixCodePage: React.FC<MatrixCodeContextProps> = ({
  inputUrl,
  isValidInput,
  qrUrl,
  displayCode,
  fileExtension,
  update,
  updateFileExtension,
  onGenerateMatrixCode,
  onDownloadMatrixCode,
}) => {
  const downloadMatrixCodeModal = useModal();
  const [selectedSize, setSelectedSize] = React.useState(M_SIZE);
  const [size, setSize] = React.useState(M_SIZE);
  const [selectedIncludeMargin, setSelectedIncludeMargin] = React.useState(
    NOT_SELECTED_INCLUDE_MARGIN,
  );
  const [includeMargin, setIncludeMargin] = React.useState(false);
  const { queryUrl } = parseQueryOfLocation(useLocation()) as {
    queryUrl: string | undefined;
  };
  const displayError = !isValidInput && inputUrl !== "";
  React.useEffect(() => {
    if (queryUrl) {
      const decodedUrl = decodeURIComponent(queryUrl);
      update(decodedUrl, decodedUrl, true);
    }
  }, [queryUrl, update]);

  const onClick = () => {
    setSize(selectedSize);
    setIncludeMargin(selectedIncludeMargin === SELECTED_INCLUDE_MARGIN);
    onGenerateMatrixCode();
  };

  return (
    <GrafferUitlsTemplate breadcrumbs={[{ to: "#", title: "スマート申請" }]}>
      <h1 className="title">
        <i className="fas fa-qrcode" /> 二次元コード生成
      </h1>
      <section className="section px-0 py-0">
        {StaticFeatureFlags.IS_SANDBOX ? (
          <>
            <WarningBanner message="サンドボックス環境のため、二次元コードは公開しないようご注意ください" />
            <div className="is-size-6 mt-4 mb-4">
              以下のURLに紐付く二次元コードが生成できます。
              <br />
              手続きを公開した後、インターネット環境から二次元コードを読み取るとページが確認できます。
            </div>
          </>
        ) : (
          <div className="is-size-6 mb-4">
            以下のURLに紐付く二次元コードが生成できます。
            <br />
            手続きを公開した後、インターネット環境から二次元コードを読み取るとページが確認できます。URLを変更した場合、この二次元コードは使用できなくなるため、新しい二次元コードを生成する必要があります。
          </div>
        )}
        <div className="field">
          <label className="label">URL</label>
          <div className="control">
            <CopyTextBox value={inputUrl} />
            {displayError && (
              <p className="help is-danger">正しいURLを入力してください。</p>
            )}
          </div>
        </div>
        <div className="field">
          <RadioButton
            label={"サイズ"}
            options={[
              { name: "S(128px)", value: S_SIZE },
              { name: "M(256px)", value: M_SIZE },
              { name: "L(512px)", value: L_SIZE },
            ]}
            value={selectedSize}
            description={""}
            direction="row"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSelectedSize(+event.target.value);
            }}
            required
          />
        </div>
        <div className="field">
          <RadioButton
            label={"余白"}
            options={[
              {
                name: NOT_SELECTED_INCLUDE_MARGIN,
                value: NOT_SELECTED_INCLUDE_MARGIN,
              },
              {
                name: SELECTED_INCLUDE_MARGIN,
                value: SELECTED_INCLUDE_MARGIN,
              },
            ]}
            value={selectedIncludeMargin}
            description={""}
            direction="row"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSelectedIncludeMargin(event.target.value);
            }}
            required
          />
        </div>
        <div className="field">
          <RadioButton
            label={"拡張子"}
            options={[
              { name: JPEG, value: JPEG },
              { name: PNG, value: PNG },
            ]}
            value={fileExtension}
            description={""}
            direction="row"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              updateFileExtension(
                event.target.value as MatrixCodeFileExtension,
              );
            }}
            required
          />
        </div>
        <p className="control">
          {displayCode ? (
            <SecondaryButton
              text={"二次元コードを更新する"}
              disabled={displayError}
              onClick={onClick}
            />
          ) : (
            <PrimaryButton
              text={"二次元コードを生成する"}
              disabled={displayError}
              onClick={onClick}
            />
          )}
        </p>
      </section>
      {displayCode && (
        <section className="section px-0">
          <div className="title is-size-4 mb-4">二次元コード</div>
          <div
            style={{
              border: `${includeMargin ? "1px solid #333" : ""}`,
              display: "inline-block",
              width: `${size + 2}px`,
              height: `${size + 2}px`,
            }}
          >
            <QRCodeCanvas
              value={qrUrl}
              includeMargin={includeMargin}
              size={size}
            />
          </div>
          <p className="control mt-4">
            <PrimaryButton
              text={"ダウンロード"}
              onClick={downloadMatrixCodeModal.open}
              disabled={!displayCode}
            />
          </p>
        </section>
      )}
      <DownloadMatrixCodeModal
        isActive={downloadMatrixCodeModal.isActive}
        href={qrUrl}
        onDownloadMatrixCode={onDownloadMatrixCode}
        onModal={downloadMatrixCodeModal.close}
      />
    </GrafferUitlsTemplate>
  );
};

export const MatrixCodeContainer = connectPageService(
  MatrixCodePage,
  () => new MatrixCodeService(),
);
